import { type ActionEvent, Controller } from "@hotwired/stimulus"

import * as Ga4 from "@/helpers/ga4_helper"

export default class CarouselController extends Controller {
  static targets = [
    "indicatorIcon",
    "nextButton",
    "prevButton",
    "slideList",
    "slideListItem",
    "thumbnailListItem"
  ]
  declare readonly indicatorIconTargets: HTMLElement[]
  declare readonly nextButtonTarget: HTMLButtonElement
  declare readonly prevButtonTarget: HTMLButtonElement
  declare readonly slideListTarget: HTMLOListElement
  declare readonly slideListItemTargets: HTMLLIElement[]
  declare readonly thumbnailListItemTargets: HTMLLIElement[]

  static values = { name: String, selectedIndex: Number }
  declare readonly nameValue: string
  declare selectedIndexValue: number

  declare private firstSlideObserver: IntersectionObserver
  declare private lastSlideObserver: IntersectionObserver

  initialize(): void {
    this.firstSlideObserver = this.slideObserver(this.prevButtonTarget)
    this.lastSlideObserver = this.slideObserver(this.nextButtonTarget)
  }

  connect(): void {
    this.firstSlideObserver.observe(this.slideListItemTargets.at(0)!)
    this.lastSlideObserver.observe(this.slideListItemTargets.at(-1)!)

    // If carousel already interacted with (e.g. arriving via back/forward button), scroll to selected
    if (this.hasInteracted) {
      this.scrollToSelected()
    }
  }

  disconnect(): void {
    this.firstSlideObserver.disconnect()
    this.lastSlideObserver.disconnect()
  }

  slideListItemTargetConnected(element: HTMLLIElement): void {
    // Check if this is the new last slide
    const isLast = element === this.slideListItemTargets.at(-1)
    if (isLast) {
      // Reconnect to the new last slide
      this.lastSlideObserver.disconnect()
      this.lastSlideObserver.observe(element)
    }
  }

  slideNext(): void {
    this.setSelectedIndex(this.selectedIndexValue + 1)
    Ga4.carouselClickEvent(this.nameValue, "Next")
  }

  slidePrev(): void {
    this.setSelectedIndex(this.selectedIndexValue - 1)
    Ga4.carouselClickEvent(this.nameValue, "Previous")
  }

  slideTo({ params: { index } }: ActionEvent): void {
    window.requestAnimationFrame(() => this.setSelectedIndex(index))
  }

  private clampIndex(index: number): number {
    return Math.max(0, Math.min(index, this.slideListItemTargets.length - 1))
  }

  private get hasInteracted(): boolean {
    return this.slideListItemTargets.some(
      (slide, index) => index > 0 && slide.getAttribute("aria-hidden") === "false"
    )
  }

  private get isAllSlidesLoaded(): boolean {
    return this.slideListItemTargets.every((slide) => !slide.hidden)
  }

  private scrollToSelected(): void {
    const selectedSlide = this.slideListItemTargets[this.selectedIndexValue]
    const selectedThumbnail = this.thumbnailListItemTargets[this.selectedIndexValue]

    ;[selectedSlide, selectedThumbnail].forEach((item) =>
      item?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "start"
      })
    )
  }

  private setSelectedIndex(index: number): void {
    if (!this.isAllSlidesLoaded) {
      this.slideListItemTargets.forEach((slide) => (slide.hidden = false))
    }

    this.selectedIndexValue = this.clampIndex(index)
    this.scrollToSelected()
    this.toggleAttributes()
  }

  private slideObserver(navButton: HTMLButtonElement): IntersectionObserver {
    return new IntersectionObserver(
      (entries) => {
        entries.forEach(({ isIntersecting }) => (navButton.disabled = isIntersecting))
      },
      { root: this.slideListTarget, threshold: 0.9 }
    )
  }

  private toggleAttributes(): void {
    this.slideListItemTargets.forEach((slide, index) => {
      const isSelected = index === this.selectedIndexValue

      slide.setAttribute("aria-hidden", isSelected ? "false" : "true")
      this.indicatorIconTargets[index]?.classList.toggle("fa-dot-circle", isSelected)
      this.thumbnailListItemTargets[index]?.classList.toggle("--selected", isSelected)
    })
  }
}
